const UNIT_DEFINITIONS = {
  tsp: { aliases: ['tsp', 'tsps', 'teaspoon', 'teaspoons'], group: 'volume', factor: 5 },
  tbsp: { aliases: ['tbsp', 'tbsps', 'tablespoon', 'tablespoons'], group: 'volume', factor: 15 },
  cup: { aliases: ['cup', 'cups'], group: 'volume', factor: 240 },
  ml: { aliases: ['ml', 'milliliter', 'milliliters'], group: 'volume', factor: 1 },
  l: { aliases: ['l', 'liter', 'liters', 'litre', 'litres'], group: 'volume', factor: 1000 },
  pint: { aliases: ['pint', 'pints'], group: 'volume', factor: 473 },
  quart: { aliases: ['quart', 'quarts'], group: 'volume', factor: 946 },
  gallon: { aliases: ['gallon', 'gallons'], group: 'volume', factor: 3785 },
  g: { aliases: ['g', 'gram', 'grams'], group: 'weight', factor: 1 },
  kg: { aliases: ['kg', 'kilogram', 'kilograms'], group: 'weight', factor: 1000 },
  oz: { aliases: ['oz', 'ozs', 'ounce', 'ounces'], group: 'weight', factor: 28.35 },
  lb: { aliases: ['lb', 'lbs', 'pound', 'pounds'], group: 'weight', factor: 453.59 },
  piece: { aliases: ['piece', 'pieces', 'item', 'items', 'each', 'pc', 'pcs'], group: 'count', factor: 1 },
  clove: { aliases: ['clove', 'cloves'], group: 'count', factor: 1 },
  slice: { aliases: ['slice', 'slices'], group: 'count', factor: 1 },
  can: { aliases: ['can', 'cans'], group: 'container', factor: 1 },
  bottle: { aliases: ['bottle', 'bottles'], group: 'container', factor: 1 },
  jar: { aliases: ['jar', 'jars'], group: 'container', factor: 1 },
  bag: { aliases: ['bag', 'bags'], group: 'container', factor: 1 },
  box: { aliases: ['box', 'boxes'], group: 'container', factor: 1 },
  package: { aliases: ['package', 'packages', 'pack'], group: 'container', factor: 1 },
  container: { aliases: ['container', 'containers'], group: 'container', factor: 1 },
  bunch: { aliases: ['bunch', 'bunches'], group: 'container', factor: 1 },
  head: { aliases: ['head', 'heads'], group: 'container', factor: 1 },
  stick: { aliases: ['stick', 'sticks'], group: 'count', factor: 1 }
};

const TEXT_QUANTITIES = {
  a: 1,
  an: 1,
  one: 1,
  some: 0.5,
  few: 0.25,
  several: 0.75,
  many: 1.5
};

function parseNumber(valueText) {
  if (!valueText) return 0;

  const cleaned = valueText.replace(',', '.').trim();

  if (/^\d+\s+\d+\/\d+$/.test(cleaned)) {
    const [whole, fraction] = cleaned.split(/\s+/);
    const [numerator, denominator] = fraction.split('/');
    return parseFloat(whole) + (parseFloat(numerator) / parseFloat(denominator));
  }

  if (/^\d+\/\d+$/.test(cleaned)) {
    const [numerator, denominator] = cleaned.split('/');
    return parseFloat(numerator) / parseFloat(denominator);
  }

  const parsed = parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function findUnitDefinition(unitText = '') {
  const normalized = unitText.trim().toLowerCase();
  if (!normalized) return null;

  return Object.entries(UNIT_DEFINITIONS).find(([, definition]) =>
    definition.aliases.includes(normalized)
  ) || null;
}

export function parseQuantityText(quantity) {
  if (quantity === null || quantity === undefined) {
    return {
      value: 0,
      unit: '',
      normalizedUnit: '',
      group: 'unknown',
      baseValue: 0,
      originalText: '',
      isGeneric: true
    };
  }

  const originalText = String(quantity).trim();
  if (!originalText) {
    return {
      value: 0,
      unit: '',
      normalizedUnit: '',
      group: 'unknown',
      baseValue: 0,
      originalText: '',
      isGeneric: true
    };
  }

  const textMatch = originalText.toLowerCase().match(/^(a|an|one|some|few|several|many)\b(.*)$/);
  if (textMatch) {
    const value = TEXT_QUANTITIES[textMatch[1]] || 0;
    const unitText = textMatch[2].trim();
    const unitDefinition = findUnitDefinition(unitText.split(/\s+/)[0] || '');

    return {
      value,
      unit: unitText,
      normalizedUnit: unitDefinition ? unitDefinition[0] : '',
      group: unitDefinition ? unitDefinition[1].group : (unitText ? 'container' : 'unknown'),
      baseValue: unitDefinition ? value * unitDefinition[1].factor : value,
      originalText,
      isGeneric: !unitDefinition
    };
  }

  const match = originalText.match(/^((?:\d+\s+\d+\/\d+)|(?:\d+\/\d+)|(?:\d+[.,]\d+)|(?:\d+))\s*(.*)$/);
  if (!match) {
    return {
      value: 0,
      unit: originalText,
      normalizedUnit: '',
      group: 'unknown',
      baseValue: 0,
      originalText,
      isGeneric: true
    };
  }

  const value = parseNumber(match[1]);
  const unitText = (match[2] || '').trim();
  const unitToken = unitText.split(/\s+/)[0] || '';
  const unitDefinitionEntry = findUnitDefinition(unitToken);
  const unitDefinition = unitDefinitionEntry ? unitDefinitionEntry[1] : null;
  const normalizedUnit = unitDefinitionEntry ? unitDefinitionEntry[0] : '';
  const group = unitDefinition ? unitDefinition.group : (unitText ? 'container' : 'count');

  return {
    value,
    unit: unitText,
    normalizedUnit,
    group,
    baseValue: unitDefinition ? value * unitDefinition.factor : value,
    originalText,
    isGeneric: !unitDefinition || group === 'container'
  };
}

export function formatQuantityValue(value) {
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/\.?0+$/, '');
}

export function hasPositiveQuantity(quantity) {
  const parsed = parseQuantityText(quantity);
  if (parsed.value > 0) return true;

  const raw = String(quantity || '').trim().toLowerCase();
  return !!raw && !/^(0|0\.0+|none|empty|out of stock|n\/a|na|-)$/.test(raw);
}

export function normalizeIngredientName(name) {
  if (!name) return '';

  return String(name)
    .toLowerCase()
    .trim()
    .replace(/^\d+\s+\d+\/\d+\s*/, '')
    .replace(/^\d+\/\d+\s*/, '')
    .replace(/^\d+[.,]?\d*\s*/, '')
    .replace(/\b(tsp|tsps|tbsp|tbsps|cup|cups|lb|lbs|oz|ozs|kg|g|ml|l|pound|pounds|ounce|ounces|kilogram|kilograms|gram|grams|milliliter|milliliters|liter|liters|litre|litres|teaspoon|teaspoons|tablespoon|tablespoons|pint|pints|quart|quarts|gallon|gallons|can|cans|bunch|bunches|head|heads|clove|cloves|slice|slices|piece|pieces|stick|sticks|bag|bags|box|boxes|jar|jars|bottle|bottles|package|packages|container|containers|fluid|fl)\b/g, '')
    .replace(/\b(chopped|diced|sliced|minced|grated|shredded|crushed|ground|finely|coarsely|roughly|thinly|thickly|freshly|optional|for garnish|for serving|to taste|of)\b/g, '')
    .replace(/[^\w\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function ingredientsMatch(name1, name2) {
  const normalized1 = normalizeIngredientName(name1);
  const normalized2 = normalizeIngredientName(name2);

  if (!normalized1 || !normalized2) return false;
  if (normalized1 === normalized2) return true;
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) return true;

  const words1 = normalized1.split(' ').filter(word => word.length > 2);
  const words2 = normalized2.split(' ').filter(word => word.length > 2);
  if (words1.length === 0 || words2.length === 0) return false;

  const matchingWords = words1.filter(word1 =>
    words2.some(word2 =>
      word1 === word2 ||
      word1.includes(word2) ||
      word2.includes(word1) ||
      word1.startsWith(word2.replace(/s$/, '')) ||
      word2.startsWith(word1.replace(/s$/, ''))
    )
  );

  return matchingWords.length / Math.min(words1.length, words2.length) >= 0.5;
}

export function parseIngredientText(ingredientText) {
  const originalText = String(ingredientText || '').trim();
  if (!originalText) {
    return { name: '', quantityText: '', quantity: parseQuantityText('') };
  }

  const match = originalText.match(/^((?:\d+\s+\d+\/\d+)|(?:\d+\/\d+)|(?:\d+[.,]?\d*)|(?:a|an|one|some|few|several|many))\s+(.*)$/i);
  if (!match) {
    return {
      name: originalText,
      quantityText: '',
      quantity: parseQuantityText('')
    };
  }

  const quantityText = match[1];
  let remainder = match[2].trim();
  const unitToken = remainder.split(/\s+/)[0] || '';
  const unitDefinition = findUnitDefinition(unitToken);
  if (unitDefinition) {
    remainder = remainder.split(/\s+/).slice(1).join(' ').trim();
  }

  const fullQuantityText = unitDefinition ? `${quantityText} ${unitToken}` : quantityText;

  return {
    name: remainder || match[2].trim(),
    quantityText: fullQuantityText.trim(),
    quantity: parseQuantityText(fullQuantityText.trim())
  };
}

export function compareRecipeIngredientToPantry(recipeIngredientText, pantryItem) {
  const recipe = parseIngredientText(recipeIngredientText);
  const pantry = parseQuantityText(pantryItem?.quantity || '');
  const pantryHasQuantity = hasPositiveQuantity(pantryItem?.quantity || '');

  if (!ingredientsMatch(recipe.name, pantryItem?.name || '')) {
    return { matches: false, hasEnough: false, comparable: false, pantryHasQuantity: false };
  }

  if (!pantryHasQuantity) {
    return {
      matches: true,
      hasEnough: false,
      comparable: false,
      pantryHasQuantity: false,
      shortageText: `${recipeIngredientText} (out of stock)`
    };
  }

  if (recipe.quantity.value === 0) {
    return {
      matches: true,
      hasEnough: true,
      comparable: false,
      pantryHasQuantity: true,
      pantryQuantityText: pantry.originalText || String(pantryItem.quantity || '')
    };
  }

  const sameGroup = recipe.quantity.group !== 'unknown' &&
    recipe.quantity.group === pantry.group &&
    recipe.quantity.baseValue > 0 &&
    pantry.baseValue > 0;

  if (sameGroup) {
    const hasEnough = pantry.baseValue >= recipe.quantity.baseValue;
    const portions = recipe.quantity.baseValue > 0
      ? Math.floor(pantry.baseValue / recipe.quantity.baseValue)
      : 1;
    const shortageValue = Math.max(0, recipe.quantity.baseValue - pantry.baseValue);
    const shortageInRecipeUnit = recipe.quantity.value > 0 && recipe.quantity.baseValue > 0
      ? shortageValue / (recipe.quantity.baseValue / recipe.quantity.value)
      : 0;

    return {
      matches: true,
      hasEnough,
      comparable: true,
      pantryHasQuantity: true,
      portions,
      pantryQuantityText: pantry.originalText || String(pantryItem.quantity || ''),
      shortageText: hasEnough
        ? ''
        : `${recipeIngredientText} (need ${shortageInRecipeUnit.toFixed(1)} more)`
    };
  }

  if (pantry.value > 0 && (pantry.isGeneric || pantry.group === 'count' || pantry.group === 'container')) {
    return {
      matches: true,
      hasEnough: true,
      comparable: false,
      pantryHasQuantity: true,
      portions: 1,
      pantryQuantityText: pantry.originalText || String(pantryItem.quantity || '')
    };
  }

  return {
    matches: true,
    hasEnough: false,
    comparable: false,
    pantryHasQuantity: true,
    pantryQuantityText: pantry.originalText || String(pantryItem.quantity || ''),
    shortageText: `${recipeIngredientText} (check pantry quantity)`
  };
}
